import { useEffect, useRef, useState } from "react";
import { http, API } from "../api";
import { FileScan, Upload, CheckCircle2, Users, Image as ImageIcon, FileText, Landmark, MapPin } from "lucide-react";
import { PageHeader, EmptyState, FormRow, Modal } from "../shared";

export default function VoterRoll({ pageContext, clearPageContext, user }) {
  const [scans, setScans] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [msg, setMsg] = useState("");
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [imgOpen, setImgOpen] = useState(null);
  const [meta, setMeta] = useState({ polling_station_name: "", polling_station_address: "", roll_date: "", booth: "" });
  const [liveCounters, setLiveCounters] = useState(null);
  // Phase 30.6 — bulk booth-change for a scan's imported voters
  const [rebooth, setRebooth] = useState(null); // { scan, new_booth, new_ward, saving, result }
  const pollIvRef = useRef(null);

  const openRebooth = (scan) => setRebooth({
    scan,
    new_booth: scan.booth || "",
    new_ward: scan.ward || "",
    saving: false,
    result: null,
  });

  const applyRebooth = async () => {
    if (!rebooth || !rebooth.new_booth?.trim()) return;
    setRebooth((s) => ({ ...s, saving: true, result: null }));
    try {
      const payload = { new_booth: rebooth.new_booth.trim() };
      if (rebooth.new_ward !== "" && rebooth.new_ward !== null && rebooth.new_ward !== undefined) {
        payload.new_ward = Number(rebooth.new_ward);
      }
      const r = await http.post(`/voter-roll/scans/${rebooth.scan.scan_id}/rebooth`, payload);
      setRebooth((s) => ({ ...s, saving: false, result: r.data }));
      load();  // refresh linked count + booth on the scan row
    } catch (e) {
      setRebooth((s) => ({ ...s, saving: false, result: { error: e.response?.data?.detail || e.message } }));
    }
  };

  const load = () => http.get("/voter-roll/scans").then((r) => setScans(r.data));
  const loadGallery = () => http.get("/voter-roll/gallery").then((r) => setGallery(r.data)).catch(() => {});
  useEffect(() => { load(); loadGallery(); return () => { if (pollIvRef.current) clearInterval(pollIvRef.current); }; }, []);

  // Deep-link from the notification bell: open a specific scan's preview.
  useEffect(() => {
    const openScanFromContext = async () => {
      if (!pageContext?.scan_id) return;
      try {
        const r = await http.get(`/voter-roll/scans/${pageContext.scan_id}`);
        const d = r.data;
        setPreview({
          scan_id: d.scan_id, is_pdf: d.is_pdf,
          pages: (d.pages || []).map((p) => ({ ...p, voters: (d.voters || []).filter((v) => v.page_index === p.page_index) })),
          voter_count: (d.voters || []).length,
          polling_station_name: d.polling_station_name,
        });
        setMsg(`Opened ${d.polling_station_name || "roll"} — ${(d.voters || []).length} voters across ${(d.pages || []).length} pages.`);
      } catch { setMsg("Could not load that scan (it may have been deleted)."); }
      finally { clearPageContext && clearPageContext(); }
    };
    openScanFromContext();
  }, [pageContext?.scan_id]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!meta.polling_station_name.trim()) {
      setMsg("Please enter the polling station name first.");
      e.target.value = ""; return;
    }
    setBusy(true); setMsg(""); setPreview(null); setLiveCounters(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("polling_station_name", meta.polling_station_name);
    fd.append("polling_station_address", meta.polling_station_address);
    fd.append("roll_date", meta.roll_date);
    fd.append("booth", meta.booth);
    try {
      const r = await http.post("/voter-roll/ocr", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 });
      if (r.data.status === "processing") {
        const reup = r.data.reupload_of ? " (re-upload — only new voters will be imported)" : "";
        setMsg(`Queued ${r.data.total_pages} page${r.data.total_pages === 1 ? "" : "s"} for OCR${reup} — processing one page at a time.`);
        setLiveCounters({
          scan_id: r.data.scan_id, processed: 0, total: r.data.total_pages,
          voters_found: 0, created: 0, matched: 0, dup_pages: 0, status: "processing",
        });
        pollScan(r.data.scan_id);
      } else {
        setPreview(r.data);
        const total = r.data.voter_count ?? (r.data.voters?.length || 0);
        setMsg(`Extracted ${total} voter row${total === 1 ? "" : "s"} across ${r.data.pages?.length || 1} page${(r.data.pages?.length || 1) === 1 ? "" : "s"} from ${r.data.polling_station_name || "the roll"}.`);
        load(); loadGallery();
      }
    } catch (err) {
      setMsg(`OCR failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setBusy(false); e.target.value = "";
    }
  };

  const pollScan = (scan_id) => {
    let tries = 0;
    if (pollIvRef.current) clearInterval(pollIvRef.current);
    pollIvRef.current = setInterval(async () => {
      tries += 1;
      try {
        const r = await http.get(`/voter-roll/scans/${scan_id}`);
        const d = r.data;
        setLiveCounters({
          scan_id,
          processed: d.processed_pages || 0,
          total: d.total_pages || 0,
          voters_found: d.voters_found || 0,
          created: d.profiles_created || 0,
          matched: d.profiles_matched || 0,
          dup_pages: d.duplicate_pages_skipped || 0,
          status: d.status,
        });
        if (d.status === "ready") {
          clearInterval(pollIvRef.current); pollIvRef.current = null;
          setPreview({
            scan_id: d.scan_id, is_pdf: d.is_pdf,
            pages: (d.pages || []).map((p) => ({ ...p, voters: (d.voters || []).filter((v) => v.page_index === p.page_index) })),
            voter_count: (d.voters || []).length,
            polling_station_name: d.polling_station_name,
          });
          setMsg(`Ready — ${d.voters_found || 0} voters found · ${d.profiles_created || 0} new profiles · ${d.profiles_matched || 0} matched${d.duplicate_pages_skipped ? ` · ${d.duplicate_pages_skipped} duplicate page(s) skipped` : ""}.`);
          load(); loadGallery();
        } else if (d.status === "error") {
          clearInterval(pollIvRef.current); pollIvRef.current = null;
          setMsg(`Background OCR failed: ${d.error || "unknown error"}`);
        } else {
          setMsg(`Processing page ${d.processed_pages || 0} / ${d.total_pages || "?"}…`);
          if (tries > 120) { clearInterval(pollIvRef.current); pollIvRef.current = null; setMsg("Timed out waiting for OCR. Refresh Recent Scans in a minute."); }
        }
      } catch {
        clearInterval(pollIvRef.current); pollIvRef.current = null;
        setMsg("Lost connection to scan status.");
      }
    }, 3000);
  };

  const isSuper = user?.role === "Super Admin";
  const [backfillState, setBackfillState] = useState(null); // {running, result}
  const runBackfill = async () => {
    if (!window.confirm("Re-scan every stored voter roll and back-fill guardian names on contacts that missed them? Existing guardian data will not be overwritten.")) return;
    setBackfillState({ running: true, result: null });
    try {
      const r = await http.post("/voter-roll/backfill-guardians");
      setBackfillState({ running: false, result: r.data });
    } catch (e) {
      setBackfillState({ running: false, result: { error: e.response?.data?.detail || e.message } });
    }
  };

  const allVotersInPreview = (preview?.pages || []).flatMap((p) => (p.voters || []).map((v) => ({ ...v, page_index: p.page_index })));

  return (
    <>
      <PageHeader eyebrow="ELECTORAL ROLL LIBRARY" title="Voter roll OCR"
        subtitle="Upload a JPG/PNG page or a full PDF — every page is stored in the gallery and each voter is back-linked to the polling station"
        actions={<>
          <button className="outline" data-testid="voter-roll-open-gallery" onClick={() => setGalleryOpen(true)}>
            <ImageIcon size={14} /> Gallery ({gallery.length})
          </button>
          {isSuper && (
            <button className="outline" data-testid="voter-roll-backfill-guardians"
              onClick={runBackfill} disabled={backfillState?.running}
              title="Re-parse every stored voter roll and fill guardian names on contacts that missed them">
              <Users size={14} /> {backfillState?.running ? "Backfilling…" : "Backfill guardians"}
            </button>
          )}
        </>} />

      {backfillState?.result && (
        <div data-testid="backfill-result-strip" className="notice-strip" style={{
          margin: "10px 20px",
          background: backfillState.result.error ? "#fdecea" : "#ecfdf5",
          color: backfillState.result.error ? "#b04a3f" : "#065f46",
          borderColor: backfillState.result.error ? "#f5c2b8" : "#a7f3d0",
        }}>
          {backfillState.result.error ? (
            <span>Backfill failed: {backfillState.result.error}</span>
          ) : (
            <span>
              ✓ Scanned <b>{backfillState.result.scans}</b> roll{backfillState.result.scans === 1 ? "" : "s"} · parsed{" "}
              <b>{backfillState.result.voters_parsed}</b> voter row{backfillState.result.voters_parsed === 1 ? "" : "s"} ·
              filled guardian on <b>{backfillState.result.contacts_touched}</b> profile{backfillState.result.contacts_touched === 1 ? "" : "s"}.
            </span>
          )}
        </div>
      )}

      <section className="panel" data-testid="voter-roll-form">
        <div className="panel-head">
          <div>
            <h2><Landmark size={16} style={{ verticalAlign: "middle" }} /> Polling Station Details</h2>
            <p>Every voter imported below inherits these fields.</p>
          </div>
        </div>
        <div className="form-grid" style={{ padding: "0 4px" }}>
          <FormRow label="Polling station name *"><input data-testid="ps-name-input" placeholder="e.g. Nabadwip Bidyapith Primary School" value={meta.polling_station_name} onChange={(e) => setMeta({ ...meta, polling_station_name: e.target.value })} /></FormRow>
          <FormRow label="Roll date"><input data-testid="ps-date-input" type="date" value={meta.roll_date} onChange={(e) => setMeta({ ...meta, roll_date: e.target.value })} /></FormRow>
          <FormRow label="Booth / Part No."><input data-testid="ps-booth-input" placeholder="42" value={meta.booth} onChange={(e) => setMeta({ ...meta, booth: e.target.value })} /></FormRow>
        </div>
        <FormRow label="Polling station address"><input data-testid="ps-address-input" placeholder="Street, PO, District, PIN" value={meta.polling_station_address} onChange={(e) => setMeta({ ...meta, polling_station_address: e.target.value })} /></FormRow>
        <div className="form-actions" style={{ marginTop: 8 }}>
          <label className="primary" data-testid="voter-roll-upload-button" style={{ cursor: "pointer" }}>
            <Upload size={16} /> {busy ? "Processing…" : "Upload page or PDF"}
            <input type="file" style={{ display: "none" }} accept="image/*,application/pdf,.pdf" onChange={upload} disabled={busy} data-testid="voter-roll-file-input" />
          </label>
        </div>
      </section>

      {msg && <div className="notice-strip" data-testid="voter-roll-msg"><CheckCircle2 size={16} />{msg}</div>}

      {liveCounters && (
        <section className="vr-live-strip" data-testid="voter-roll-live-counters">
          <div className="vr-live-progress">
            <div className="vr-live-bar">
              <div
                className="vr-live-bar-fill"
                style={{ width: `${liveCounters.total ? Math.round((liveCounters.processed / liveCounters.total) * 100) : 0}%` }}
              />
            </div>
            <div className="vr-live-progress-label" data-testid="vr-live-progress-label">
              Pages <b>{liveCounters.processed}</b> / {liveCounters.total}
            </div>
          </div>
          <ul className="vr-live-counters">
            <li data-testid="vr-live-found"><span>Voters found</span><b>{liveCounters.voters_found}</b></li>
            <li className="ok" data-testid="vr-live-created"><span>New profiles</span><b>{liveCounters.created}</b></li>
            <li className="mute" data-testid="vr-live-matched"><span>Matched</span><b>{liveCounters.matched}</b></li>
            <li className="warn" data-testid="vr-live-dup"><span>Duplicate pages skipped</span><b>{liveCounters.dup_pages}</b></li>
          </ul>
        </section>
      )}

      {preview && (
        <section className="panel" data-testid="voter-roll-preview">
          <div className="panel-head">
            <div>
              <h2>Preview — {allVotersInPreview.length} voters extracted</h2>
              <p>{preview.polling_station_name} · {preview.pages?.length || 1} page{(preview.pages?.length || 1) === 1 ? "" : "s"} · <b>Auto-linked to CRM</b></p>
            </div>
          </div>
          <div className="finance-table">
            <div className="tr th"><span>Sl.</span><span>Name</span><span>Relation</span><span>Age / Sex</span><span>EPIC</span><span>Page</span></div>
            {allVotersInPreview.map((v, i) => (
              <div key={i} className="tr" data-testid={`voter-preview-row-${i}`}>
                <span>{v.serial || i + 1}</span>
                <span><b>{v.name}</b><small> {v.address}</small></span>
                <span>{v.relation || "—"}</span>
                <span>{v.age || "—"} · {v.gender || "—"}</span>
                <span><code>{v.epic || "—"}</code></span>
                <span>#{v.page_index}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="panel">
        <div className="panel-head"><h2>Recent scans</h2><p>{scans.length} rolls processed</p></div>
        {scans.length === 0 ? <EmptyState icon={FileScan} title="No pages yet" hint="Fill in the polling station details above and upload a page." /> : (
          <div className="finance-table">
            <div className="tr th"><span>File</span><span>Polling station</span><span>Pages / Booth</span><span>Voters</span><span>By</span><span>Actions</span></div>
            {scans.map((s, i) => (
              <div key={s.scan_id} className="tr" data-testid={`scan-row-${i}`}>
                <span><b>{s.filename}</b><small> {new Date(s.created_at).toLocaleString()}</small>{s.is_pdf && <><br /><small style={{ color: "#0f766e" }}><FileText size={11} /> PDF</small></>}</span>
                <span>{s.polling_station_name || "—"}<br /><small>{s.roll_date || ""}</small></span>
                <span>{(s.pages || []).length || 1} page · Booth {s.booth || "—"}</span>
                <span><b>{s.voters?.length || 0}</b>{s.linked_contact_count !== undefined && <><br /><small style={{ color: "#0f766e" }}>{s.linked_contact_count} linked</small></>}</span>
                <span>{s.uploaded_by}</span>
                <span data-testid={`scan-summary-${i}`} style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                  <small>{s.status === "processing" ? <em style={{ color: "#0f766e" }}>{s.processed_pages || 0}/{s.total_pages || 0}…</em> : <><b>{s.profiles_created || 0}</b> new · {s.profiles_matched || 0} matched</>}</small>
                  {(s.linked_contact_count || 0) > 0 && (
                    <button className="link" data-testid={`scan-rebooth-${i}`} onClick={() => openRebooth(s)} style={{ color: "#0f766e", fontWeight: 700 }}>
                      <MapPin size={11} /> Change booth
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {galleryOpen && (
        <Modal title="Voter Roll Gallery — Super Admin" onClose={() => setGalleryOpen(false)} testid="voter-roll-gallery-modal">
          <p className="hint">Every uploaded voter-roll page in one place — click any image to zoom.</p>
          {gallery.length === 0 ? <EmptyState icon={ImageIcon} title="Gallery is empty" hint="Uploads will appear here automatically." /> : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10, marginTop: 10 }} data-testid="voter-roll-gallery-grid">
              {gallery.map((g, i) => (
                <div key={g.file_id} data-testid={`gallery-item-${i}`} style={{ background: "#fff", border: "1px solid var(--line)", borderRadius: 10, overflow: "hidden", cursor: "pointer" }} onClick={() => setImgOpen(g)}>
                  <div style={{ height: 110, background: `#eee center/cover no-repeat url(${API}/files/${g.file_id})` }} />
                  <div style={{ padding: 8 }}>
                    <b style={{ fontSize: 12 }}>{g.scan?.polling_station_name || "Untitled roll"}</b>
                    <p className="hint" style={{ margin: "4px 0 0", fontSize: 11 }}>Page #{g.page_index} · Booth {g.scan?.booth || "—"}</p>
                    <p className="hint" style={{ margin: 0, fontSize: 10 }}>{new Date(g.created_at).toLocaleDateString()} · {Math.round((g.size || 0) / 1024)} KB</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}

      {imgOpen && (
        <Modal title={`${imgOpen.scan?.polling_station_name || "Voter roll"} · Page ${imgOpen.page_index}`} onClose={() => setImgOpen(null)} testid="voter-roll-image-modal">
          <img src={`${API}/files/${imgOpen.file_id}`} alt="voter roll page" style={{ maxWidth: "100%", borderRadius: 8, border: "1px solid var(--line)" }} />
          <p className="hint" style={{ marginTop: 8 }}>Uploaded {new Date(imgOpen.created_at).toLocaleString()} by {imgOpen.uploaded_by}</p>
        </Modal>
      )}

      {rebooth && (
        <Modal title="Change booth on voter-roll imports" onClose={() => setRebooth(null)} testid="rebooth-modal">
          <p className="hint" style={{ margin: "0 0 10px" }}>
            You&apos;re about to bulk-update <b>{rebooth.scan.linked_contact_count || 0}</b> voter profile{(rebooth.scan.linked_contact_count || 0) === 1 ? "" : "s"} imported from
            {" "}<b>{rebooth.scan.polling_station_name || "this scan"}</b>. The scan record itself will also carry the new booth.
          </p>
          <div className="form-grid">
            <FormRow label="New booth *">
              <input
                data-testid="rebooth-booth-input"
                value={rebooth.new_booth}
                onChange={(e) => setRebooth((s) => ({ ...s, new_booth: e.target.value, result: null }))}
                placeholder="e.g. 42"
                autoFocus
              />
            </FormRow>
            <FormRow label="New ward (optional)">
              <input
                data-testid="rebooth-ward-input"
                type="number"
                value={rebooth.new_ward}
                onChange={(e) => setRebooth((s) => ({ ...s, new_ward: e.target.value, result: null }))}
                placeholder="Leave blank to keep as-is"
              />
            </FormRow>
          </div>
          <p className="hint" style={{ fontSize: 11, color: "#92400e", marginTop: 4 }}>
            ⚠ This is not undoable in bulk — please double-check the number.
          </p>

          {rebooth.result && (
            <div
              data-testid="rebooth-result"
              className="notice-strip"
              style={{
                marginTop: 10,
                background: rebooth.result.error ? "#fdecea" : "#e5f2e3",
                color: rebooth.result.error ? "#b04a3f" : "#3a7647",
                borderColor: "#c1dbb9",
              }}
            >
              {rebooth.result.error ? (
                <span>Error: {rebooth.result.error}</span>
              ) : (
                <span>
                  ✓ <b>{rebooth.result.modified}</b> profile{rebooth.result.modified === 1 ? "" : "s"} moved to booth <b>{rebooth.result.new_booth}</b>
                  {rebooth.result.new_ward !== null && rebooth.result.new_ward !== undefined ? `, ward ${rebooth.result.new_ward}` : ""}.
                </span>
              )}
            </div>
          )}
          <div className="form-actions">
            <button className="outline" data-testid="rebooth-cancel" onClick={() => setRebooth(null)}>Close</button>
            <button
              className="primary"
              data-testid="rebooth-apply"
              disabled={rebooth.saving || !rebooth.new_booth?.trim() || rebooth.result?.modified >= 0}
              onClick={applyRebooth}
            >
              <MapPin size={14} /> {rebooth.saving ? "Updating…" : "Apply new booth"}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
